export class CatalogValidationError extends Error {
  constructor(issues) {
    super(`Invalid TESTCS building catalog:\n${issues.map(issue => `- ${issue}`).join('\n')}`)
    this.name = 'CatalogValidationError'
    this.issues = issues
  }
}

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)
const isVector = (value, size = 3) =>
  Array.isArray(value) && value.length === size && value.every(item => typeof item === 'number' && Number.isFinite(item))

export function validateCatalog(raw) {
  const issues = []
  if (!isObject(raw)) return { ok: false, issues: ['catalog must be an object'] }
  if (raw.schemaVersion !== 1) issues.push('schemaVersion must equal 1')
  if (typeof raw.catalogId !== 'string' || !raw.catalogId) issues.push('catalogId must be a non-empty string')
  if (raw.units !== 'meters') issues.push('units must equal "meters"')

  const grid = raw.grid
  if (!isObject(grid)) issues.push('grid must be an object')
  else {
    for (const key of ['horizontalStepM', 'verticalStepM', 'rotationStepDeg', 'snapToleranceM']) {
      if (!(typeof grid[key] === 'number' && Number.isFinite(grid[key]) && grid[key] > 0)) {
        issues.push(`grid.${key} must be a positive finite number`)
      }
    }
    if (Number.isFinite(grid.rotationStepDeg) && 360 % grid.rotationStepDeg !== 0) {
      issues.push('grid.rotationStepDeg must divide 360')
    }
  }

  if (!isObject(raw.snapTypes)) issues.push('snapTypes must be an object')
  const ids = new Set()
  if (!Array.isArray(raw.modules) || raw.modules.length === 0) issues.push('modules must be a non-empty array')
  else {
    raw.modules.forEach((module, index) => {
      const label = `modules[${index}]`
      if (!isObject(module)) {
        issues.push(`${label} must be an object`)
        return
      }
      if (typeof module.id !== 'string' || !module.id) issues.push(`${label}.id must be a non-empty string`)
      else if (ids.has(module.id)) issues.push(`${label}.id duplicates "${module.id}"`)
      else ids.add(module.id)
      if (typeof module.category !== 'string' || !module.category) issues.push(`${label}.category must be a string`)
      if (typeof module.displayName !== 'string' || !module.displayName) issues.push(`${label}.displayName must be a string`)
      if (!isVector(module.dimensionsM) || module.dimensionsM.some(value => value <= 0)) {
        issues.push(`${label}.dimensionsM must contain three positive numbers`)
      }
      if (!Array.isArray(module.snapPoints)) issues.push(`${label}.snapPoints must be an array`)
      else {
        const snapIds = new Set()
        module.snapPoints.forEach((point, pointIndex) => {
          const pointLabel = `${label}.snapPoints[${pointIndex}]`
          if (!isObject(point) || typeof point.id !== 'string' || typeof point.type !== 'string') {
            issues.push(`${pointLabel} must include string id and type`)
            return
          }
          if (snapIds.has(point.id)) issues.push(`${pointLabel}.id duplicates "${point.id}"`)
          snapIds.add(point.id)
          if (!isVector(point.positionM) || !isVector(point.normal)) issues.push(`${pointLabel} has invalid vectors`)
        })
      }
    })
  }
  return { ok: issues.length === 0, issues }
}

export function parseCatalog(raw) {
  const result = validateCatalog(raw)
  if (!result.ok) throw new CatalogValidationError(result.issues)
  const modules = raw.modules.map(module => ({ ...module }))
  const moduleById = Object.fromEntries(modules.map(module => [module.id, module]))
  const categories = [...new Set(modules.map(module => module.category))]
  return { ...raw, modules, moduleById, categories }
}

export async function loadCatalog(source) {
  const loaded = typeof source === 'function' ? await source() : await source
  const raw = loaded && typeof loaded.json === 'function' ? await loaded.json() : loaded
  return parseCatalog(raw)
}

